// Screen 5 (spec 13 §Screens, §Gate tracker; design spec §9 `/operator/gate`): the platform
// operator's gate dashboard. One API call on load, `GET /v1/operator/gate?as_of=` (the server
// fans out per company; the app never joins across companies). A count of gates per state, then
// each gate with actual vs target, due date, days left and state; the kill criteria; a baseline
// gate against its baseline; the per-DE hours table; a link to the data-entry screen.
// PLATFORM-OPERATOR: every gate, unit, condition and kill line comes from the payload (the
// operator company's metrics with a gate spec — KPI data); nothing here names a company.
// States carry an icon and a word, never colour alone. Targets are the server's (a persisted
// rule; the gate spec's value only as a labelled fallback), and a gate the server marks
// `incomplete` shows no verdict, with the reasons in a notice (S1-17 #5, #9).
import type { GateState } from "@companyos/domain-kpi";
import {
  Badge,
  Button,
  buttonVariants,
  EmptyState,
  Input,
  LocationHeader,
  Notice,
  SectionHeading,
  SettingsSection,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleXIcon,
  FlagIcon,
  OctagonAlertIcon,
  TrendingUpIcon,
  UsersIcon,
} from "lucide-react";
import type { Api } from "../../api/client";
import type { Gate, GateRow } from "../../api/types";
import { AWAITING_DATA, formatDate, formatHours, formatMoney } from "../format";
import { OpsBody, OpsFrame, operatorCrumb, Tiles } from "../ops-ux";
import { gateQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";

export const GATE_STATE_LABELS: Record<GateState, string> = {
  on_track: "On track",
  at_risk: "At risk",
  missed: "Missed",
  met: "Met",
  incomplete: "Incomplete",
};

const STATE_ICON = {
  on_track: CircleDotIcon,
  at_risk: CircleAlertIcon,
  missed: CircleXIcon,
  met: CircleCheckIcon,
  incomplete: CircleDashedIcon,
} as const;

const STATE_VARIANT = {
  on_track: "info",
  at_risk: "warning",
  missed: "danger",
  met: "success",
  incomplete: "outline",
} as const;

export function GateStateBadge({ state }: { state: GateState }) {
  const Icon = STATE_ICON[state];
  return (
    <Badge variant={STATE_VARIANT[state]} data-state={state}>
      <Icon aria-hidden="true" />
      {GATE_STATE_LABELS[state]}
    </Badge>
  );
}

/** A gate value in its unit (percent = fraction, currency = USD minor units, time = minutes). */
export function formatGateValue(unit: GateRow["unit"] | undefined, v: number | null): string {
  if (v === null) return AWAITING_DATA;
  switch (unit) {
    case "percent":
      return `${Math.round(v * 100)}%`;
    case "currency":
      return formatMoney(v, "USD", { whole: true });
    case "time":
      return formatHours(v);
    default:
      return Number.isInteger(v) ? v.toLocaleString("en-US") : v.toFixed(1);
  }
}

/** The target as the server resolved it (a persisted rule, or the flagged spec fallback). */
function targetText(g: GateRow): string {
  if (g.target === null) return "—";
  if (g.direction === "between" && g.target_max !== null)
    return `${formatGateValue(g.unit, g.target)} – ${formatGateValue(g.unit, g.target_max)}`;
  return `${g.direction === "lte" ? "≤" : "≥"} ${formatGateValue(g.unit, g.target)}`;
}

const TARGET_SOURCE_NOTE = {
  rule: null,
  verdict_fallback: "The gate's own value: no target rule is set for this gate.",
  derived: null,
  none: null,
} as const;

export function GateDashboard({
  api,
  asOf,
  onAsOf,
}: {
  api: Api;
  /** `?as_of=`; null = today (the server's HTVA calendar). */
  asOf: string | null;
  onAsOf: (asOf: string | null) => void;
}) {
  const gate = useQuery(gateQuery(api, asOf));
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Gate tracker" }]}
        icon={FlagIcon}
        title="Gate tracker"
        count={gate.data ? gate.data.gates.length : undefined}
        actions={
          gate.data?.company ? (
            <Link
              to="/c/$slug/kpis/entry/numbers"
              params={{ slug: gate.data.company.slug }}
              className={buttonVariants({ variant: "ghost", size: "sm" })}
            >
              Enter gate numbers
            </Link>
          ) : null
        }
      />
      <Toolbar bleed>
        <label htmlFor="gate-as-of" className="text-xs text-muted-foreground">
          As of
        </label>
        <Input
          id="gate-as-of"
          type="date"
          className="h-7 w-40 py-0"
          value={asOf ?? gate.data?.as_of ?? ""}
          onChange={(e) => onAsOf(e.target.value || null)}
        />
        {asOf ? (
          <Button variant="ghost" size="sm" onClick={() => onAsOf(null)}>
            Today
          </Button>
        ) : null}
        {gate.data?.company ? (
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            {gate.data.company.name}'s gates
          </span>
        ) : null}
      </Toolbar>
      <OpsBody>
        {gate.isPending ? (
          <Loading label="Loading the gate tracker" rows={6} />
        ) : gate.isError ? (
          <LoadError what="The gate tracker" error={gate.error} />
        ) : (
          <GateBody gate={gate.data} />
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function GateBody({ gate }: { gate: Gate }) {
  // A baseline gate (latest vs a baseline period) gets its own panel.
  const revenue = gate.gates.find((g) => g.measure === "baseline");
  return (
    <>
      {gate.gates.length === 0 && gate.complete ? (
        <EmptyState
          variant="card"
          icon={FlagIcon}
          title="No gates yet"
          description="A gate is a KPI of your own company with a gate set on it."
          className="mb-4"
        />
      ) : null}
      {gate.complete ? null : (
        <Notice tone="warning" className="mb-4" title="Incomplete data" data-slot="gate-incomplete">
          Gates marked Incomplete were computed from partial data and state no verdict:{" "}
          {gate.incomplete_reasons.join("; ")}.
        </Notice>
      )}
      <Tiles data-slot="gate-summary" aria-label="Gates by state">
        {(["on_track", "at_risk", "missed", "met"] as const).map((state) => (
          <StatTile
            key={state}
            size="sm"
            label={GATE_STATE_LABELS[state]}
            icon={STATE_ICON[state]}
            value={gate.gates.filter((g) => g.state === state).length}
            detail={`of ${gate.gates.length} gates`}
          />
        ))}
      </Tiles>
      <Table data-slot="gates">
        <TableCaption>Gates as of {formatDate(gate.as_of)}.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Gate</TableHead>
            <TableHead scope="col" className="text-right">
              Actual
            </TableHead>
            <TableHead scope="col" className="text-right">
              Target
            </TableHead>
            <TableHead scope="col">Due</TableHead>
            <TableHead scope="col" className="text-right">
              Days left
            </TableHead>
            <TableHead scope="col">State</TableHead>
            <TableHead scope="col">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {gate.gates.map((g) => (
            <TableRow key={g.key} data-gate={g.key}>
              <TableHead scope="row" className="font-normal">
                {g.label}
              </TableHead>
              <TableCell className="text-right tabular-nums" data-slot="actual">
                {formatGateValue(g.unit, g.actual)}
              </TableCell>
              <TableCell
                className="text-right tabular-nums"
                data-slot="target"
                data-target-source={g.target_source}
              >
                {targetText(g)}
                {TARGET_SOURCE_NOTE[g.target_source] ? (
                  <span
                    className="block text-xs text-muted-foreground"
                    title={TARGET_SOURCE_NOTE[g.target_source] ?? undefined}
                  >
                    fallback
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{g.due_on ? formatDate(g.due_on) : "—"}</TableCell>
              <TableCell className="text-right tabular-nums">{g.days_left ?? "—"}</TableCell>
              <TableCell>
                <GateStateBadge state={g.state} />
              </TableCell>
              <TableCell
                className="max-w-60 min-w-40 whitespace-normal text-muted-foreground"
                data-slot="note"
              >
                {g.note ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {gate.kill_criteria.length ? (
          <SettingsSection
            data-slot="kill-criteria"
            icon={FlagIcon}
            title="Kill criteria"
            description="The stop lines set on the gates."
          >
            <ul className="flex flex-col gap-3">
              {gate.kill_criteria.map((k) => (
                <li
                  key={k.text}
                  className="flex flex-col gap-1"
                  data-triggered={k.triggered ?? "unknown"}
                >
                  <span className="text-sm">{k.text}</span>
                  {k.triggered === null ? (
                    <Badge variant="outline">
                      <CircleDashedIcon aria-hidden="true" />
                      Unknown: incomplete data
                    </Badge>
                  ) : k.triggered ? (
                    <Badge variant="danger">
                      <OctagonAlertIcon aria-hidden="true" />
                      Triggered
                    </Badge>
                  ) : (
                    <Badge variant="success">
                      <CircleCheckIcon aria-hidden="true" />
                      Not triggered
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </SettingsSection>
        ) : null}
        {revenue ? (
          <SettingsSection
            data-slot="revenue"
            icon={TrendingUpIcon}
            title={`${revenue.label} vs baseline`}
            description={revenue.condition}
          >
            <div className="flex flex-col gap-2 text-sm">
              <p>
                Latest:{" "}
                <span className="font-medium tabular-nums">
                  {formatGateValue(revenue.unit, revenue.actual)}
                </span>
                {revenue.target !== null ? (
                  <>
                    {" "}
                    · review line{" "}
                    <span className="tabular-nums">
                      {formatGateValue(revenue.unit, revenue.target)}
                    </span>
                  </>
                ) : null}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <GateStateBadge state={revenue.state} />
                <span className="text-muted-foreground">{revenue.note ?? ""}</span>
              </div>
            </div>
          </SettingsSection>
        ) : null}
      </div>

      <section aria-labelledby="gate-per-de" className="mt-8">
        <SectionHeading
          headingId="gate-per-de"
          title="Hours per DE"
          count={gate.per_de.length}
          description="The week containing the as-of date, from each client company (approved time only)."
        />
        <PerDeTable gate={gate} />
      </section>
    </>
  );
}

function PerDeTable({ gate }: { gate: Gate }) {
  if (!gate.per_de.length)
    return (
      <EmptyState
        variant="card"
        icon={UsersIcon}
        title="No active DE seats"
        description="Client companies' DEs appear here."
      />
    );
  const sup = gate.gates.find((g) => g.fanout === "supervision");
  const founder = gate.gates.find((g) => g.fanout === "founder");
  return (
    <Table data-slot="per-de">
      <TableCaption>Supervisor and founder hours of the week, one row per active DE.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">DE</TableHead>
          <TableHead scope="col">Company</TableHead>
          <TableHead scope="col" className="text-right">
            Day
          </TableHead>
          <TableHead scope="col" className="text-right">
            Supervisor h
          </TableHead>
          <TableHead scope="col" className="text-right">
            Founder h
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {gate.per_de.map((d) => (
          <TableRow key={`${d.company.id}:${d.de.membership_id}`} data-de={d.de.membership_id}>
            <TableHead scope="row" className="font-normal">
              {d.de.display_name}
            </TableHead>
            <TableCell>{d.company.name}</TableCell>
            <TableCell className="text-right tabular-nums">{d.day}</TableCell>
            <TableCell className="text-right tabular-nums" data-minutes={d.supervisor_minutes_week}>
              {formatHours(d.supervisor_minutes_week)}
            </TableCell>
            <TableCell className="text-right tabular-nums" data-minutes={d.founder_minutes_week}>
              {formatHours(d.founder_minutes_week)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableHead scope="row" colSpan={3}>
            Mean over every active DE ({sup ? targetText(sup) : "—"} supervisor,{" "}
            {founder ? targetText(founder) : "—"} founder)
          </TableHead>
          <TableCell className="text-right tabular-nums" data-mean="supervisor">
            {formatGateValue("time", sup?.actual ?? null)}
          </TableCell>
          <TableCell className="text-right tabular-nums" data-mean="founder">
            {formatGateValue("time", founder?.actual ?? null)}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}
